import type { MeasurementKind } from '../columnUnits.ts';

/**
 * Shared column shapes — the SSOT for virtual-view column groups.
 *
 * A `Shape` names a reusable group of columns once. Each `Column` maps a
 * de-prefixed canonical (agent-facing) name to its source-side suffix plus
 * the single canonical description shown to the agent. Column *types* are
 * deliberately absent: they are derived from the source view's parsed schema
 * at construction, so a shape is never a duplicate of the generated spec.
 *
 * Source columns are always CONSTRUCTED as `prefix + suffix`, never matched
 * by `startsWith`. Scenario `systemOptimized` and `systemOptimizedChoke`
 * share a prefix boundary, so `systemOptimizedChokeBeanSize` (scenario
 * `systemOptimized`, suffix `ChokeBeanSize`) must never be confused with
 * `systemOptimizedChokeChokeBeanSize` (scenario `systemOptimizedChoke`,
 * suffix `ChokeBeanSize`).
 */

/**
 * One column in a shape. Either a flat column — one canonical (agent-facing)
 * name backed by a single source suffix — or a struct column whose canonical
 * name is an engine `struct` type assembled from several source columns, one
 * per named member.
 */
export type Column = FlatColumn | StructColumn;

/**
 * Construct a {@link FlatColumn}. `suffix` defaults to `canonical` — the common
 * passthrough case where the source column name equals the agent-facing name —
 * and an optional `valueType` override is emitted only when provided.
 */
export const col = (
  canonical: string,
  kind: MeasurementKind,
  description: string,
  opts?: { suffix?: string; valueType?: 'string' | 'number' | 'date' },
): FlatColumn => ({
  canonical,
  suffix: opts?.suffix ?? canonical,
  description,
  kind,
  ...(opts?.valueType ? { valueType: opts.valueType } : {}),
});

/** The parent-network direct-relation column shared by every NetworkRisk and
 * B3 network-grain source: a constant ref to the parent network model entity. */
export const NETWORK_DIRECT: Column = col('network', 'unitless', 'Reference to the parent network model entity.');

/** The reservoir direct-relation column shared by the reservoir-grain sources:
 * a constant ref to the Reservoir model entity. */
export const RESERVOIR_DIRECT: Column = col('reservoir', 'unitless', 'Reference to the reservoir model entity.');
/** Unconstrained potential production / injection rates per phase. Shared by
 * `WellPotential_String_Agent`, the daily rollup, and `FieldInputs_Agent`. Bare
 * source columns — `suffix === canonical`, `prefix:''`. */
export const POTENTIAL_RATES: Shape = [
  col('potentialOilProduction', 'liquidVolume', 'Unconstrained potential oil production.'),
  col('potentialGasProduction', 'gasVolumeModel', 'Unconstrained potential gas production.'),
  col('potentialWaterProduction', 'liquidVolume', 'Unconstrained potential water production.'),
  col('potentialGasInjection', 'gasVolumeModel', 'Unconstrained potential gas injection.'),
  col('potentialWaterInjection', 'liquidVolume', 'Unconstrained potential water injection.'),
];
/** Unconstrained potential production / injection volumes per phase, de-prefixed
 * to clean phase canonicals (`oil`/`gas`/`water`/`gasInjection`/`waterInjection`).
 * Shared by the daily-rollup and field-input `PotentialOutput` sub-views — both
 * `DailyRollup*_Agent` and `FieldInputs_Agent` spell the potential columns
 * identically, so one shape serves both. Potential values are VOLUMES, never
 * rates (no `*Rate` twin). */
export const POTENTIAL_OUTPUT: Shape = [
  col('oil', 'liquidVolume', 'Unconstrained potential oil production volume.', { suffix: 'potentialOilProduction' }),
  col('gas', 'gasVolumeModel', 'Unconstrained potential gas production volume.', { suffix: 'potentialGasProduction' }),
  col('water', 'liquidVolume', 'Unconstrained potential water production volume.', {
    suffix: 'potentialWaterProduction',
  }),
  col('gasInjection', 'gasVolumeModel', 'Unconstrained potential gas injection volume.', {
    suffix: 'potentialGasInjection',
  }),
  col('waterInjection', 'liquidVolume', 'Unconstrained potential water injection volume.', {
    suffix: 'potentialWaterInjection',
  }),
];

/** Planning-target production / injection volumes per phase, de-prefixed to clean
 * phase canonicals. The oil-production source suffix is the ONE column whose
 * spelling diverges between sources — `DailyRollup*_Agent` carries the
 * load-bearing `targetOilproduction` typo (lowercase `p`) while `FieldInputs_Agent`
 * correctly spells `targetOilProduction` — so it is the single parameter; every
 * other target column is spelled identically across sources. Target values are
 * VOLUMES, never rates. */
export const makeTargetOutput = (oilProductionSuffix: string): Shape => [
  col('oil', 'liquidVolume', 'Planning target oil production volume.', { suffix: oilProductionSuffix }),
  col('gas', 'gasVolumeModel', 'Planning target gas production volume.', { suffix: 'targetGasProduction' }),
  col('water', 'liquidVolume', 'Planning target water production volume.', { suffix: 'targetWaterProduction' }),
  col('gasInjection', 'gasVolumeModel', 'Planning target gas injection volume.', { suffix: 'targetGasInjection' }),
  col('waterInjection', 'liquidVolume', 'Planning target water injection volume.', { suffix: 'targetWaterInjection' }),
];

/** Reservoir-pressure estimates and their source, shared by the well-status
 * pressure-compliance view and the estimated-rates context. Bare source
 * columns — `suffix === canonical`, `prefix:''`. */
export const RESERVOIR_PRESSURE: Shape = [
  col('reservoirPressureDatum', 'pressureAbsolute', 'Reservoir pressure at the datum depth.'),
  col('reservoirPressureMidPerforation', 'pressureAbsolute', 'Reservoir pressure at the mid-perforation depth.'),
  col('pressureSource', 'unitless', 'Source of the reservoir-pressure estimate.'),
];

/** Flow-assurance condition flags raised for the producing string: the
 * five-flag core shared by the per-string outputs and the flowline outputs. */
export const FLOW_ASSURANCE_FLAGS: Shape = [
  col('asphalteneFlag', 'unitless', 'Asphaltene deposition risk flag.', { suffix: 'AsphalteneFlag' }),
  col('hydrateFlag', 'unitless', 'Hydrate formation risk flag.', { suffix: 'HydrateFlag' }),
  col('scaleFlag', 'unitless', 'Scale deposition risk flag.', { suffix: 'ScaleFlag' }),
  col('waxFlag', 'unitless', 'Wax deposition risk flag.', { suffix: 'WaxFlag' }),
  col('pe5StabilityFlag', 'unitless', 'PE5 flow-stability flag.', { suffix: 'Pe5StabilityFlag' }),
];

/** Per-string maximum-rate operating limits shared by the ESP / gas-lift limit
 * clusters and the lift-type rate limits. Bare canonical columns
 * (`suffix === canonical`, `prefix:''`). */
export const WELL_RATE_LIMITS: Shape = [
  col('maxLiquidRate', 'liquidRate', 'Maximum allowable liquid production rate.'),
  col('maxOilRate', 'liquidRate', 'Maximum allowable oil production rate.'),
  col('maxGOR', 'gasOilRatio', 'Maximum allowable gas-oil ratio.'),
];

/** A flat column: canonical (agent-facing) name ← source suffix. */
export type FlatColumn = {
  /** De-prefixed, agent-facing column name. */
  readonly canonical: string;
  /** Source-side name tail; the source column is `${prefix}${suffix}`. */
  readonly suffix: string;
  /** Canonical description surfaced to the agent — one per measure, reused across views. */
  readonly description: string;
  /** Measurement category; resolves to the column's stored unit via {@link unitForKind}. */
  readonly kind: MeasurementKind;
  /**
   * Override for the timeseries datapoint value type when the source codegen
   * cannot express it (a bare `TimeSeries` ref carries no string/numeric
   * discriminator). Honored by aligned-series views, where a TS-ref collapses to
   * a typed scalar column: `'string'` yields a unit-less string column, `'date'`
   * a unit-less timestamp column (epoch-ms instants render as dates rather than
   * raw integers). Omit to derive the type from the source schema.
   */
  readonly valueType?: 'string' | 'number' | 'date';
};

/**
 * A struct column: one canonical name carrying an engine `struct` type whose
 * members each map to a source suffix. The source columns are constructed as
 * `${prefix}${suffix}` exactly like a flat column; the canonical column nests
 * them under their member names (e.g. `fluidRate: { max, min }`).
 *
 * Two flavours, distinguished by `kind`:
 *   - a MEASURE struct (a real `kind`) groups columns that share one
 *     measurement — every member carries that unit (e.g. `fluidRate: { max,
 *     min }`, both `liquidRate`);
 *   - a GROUPING struct (`kind: 'unitless'`) bundles heterogeneous identifiers
 *     /attributes purely to de-prefix a repeated source prefix (e.g. the ESP
 *     `pump`/`motor` nameplate blocks). Members carry no unit and take their
 *     leaf type straight from the source schema.
 */
export type StructColumn = {
  /** De-prefixed, agent-facing column name carrying the struct type. */
  readonly canonical: string;
  /** Member name → source-side suffix; the source column is `${prefix}${suffix}`. */
  readonly members: Readonly<Record<string, string>>;
  /** Canonical description surfaced to the agent — one per measure, reused across views. */
  readonly description: string;
  /**
   * Measurement category shared by every member; resolves to the stored unit via
   * {@link unitForKind}. Use `'unitless'` for a grouping struct whose members are
   * heterogeneous and unit-free.
   */
  readonly kind: MeasurementKind;
};
/** Discriminator: is `c` a struct column (vs a flat one)? */
export const isStructColumn = (c: Column): c is StructColumn => 'members' in c;

/** A reusable, named group of columns. */
export type Shape = readonly Column[];
/** A measure group: a shape optionally namespaced by a column-name prefix. */
export type EnrichGroup = { readonly shape: Shape; readonly prefix?: string };

/**
 * A column resolved against a source prefix. Either a flat column — one
 * constructed source column renamed to its canonical name — or a struct column
 * whose canonical name is assembled from several constructed source columns,
 * one per member.
 */
export type Resolved = ResolvedFlat | ResolvedStruct;

/** A flat resolved column: the concrete source⇄canonical pairing. */
export type ResolvedFlat = {
  /** The constructed source column name (`${prefix}${suffix}`). */
  readonly source: string;
  /** The agent-facing canonical name. */
  readonly canonical: string;
  /** Canonical description for the agent. */
  readonly description: string;
  /** Measurement category carried from the shape; resolves to the stored unit via {@link unitForKind}. */
  readonly kind: MeasurementKind;
  /** Datapoint value-type override carried from the shape (see `FlatColumn.valueType`). */
  readonly valueType?: 'string' | 'number' | 'date';
};

/**
 * A struct resolved column: the canonical name plus its member → constructed
 * source column map. The source columns are projected and consumed by the
 * `struct` construction; only the canonical column survives on the agent surface.
 */
export type ResolvedStruct = {
  /** The agent-facing canonical name carrying the struct type. */
  readonly canonical: string;
  /** Member name → constructed source column name (`${prefix}${suffix}`). */
  readonly members: Readonly<Record<string, string>>;
  /** Canonical description for the agent. */
  readonly description: string;
  /** Measurement category shared by every member; resolves to the stored unit via {@link unitForKind}. */
  readonly kind: MeasurementKind;
};

/** Discriminator: is `c` a resolved struct column (vs a flat one)? */
export const isResolvedStruct = (c: Resolved): c is ResolvedStruct => 'members' in c;

/**
 * Resolve a shape against a source prefix. `prefix === ''` (the default)
 * yields `source === canonical` for a flat column — a passthrough with no
 * rename. A struct column resolves each member's suffix to its constructed
 * source column, leaving the canonical name to carry the assembled struct.
 */
export const resolve = (shape: Shape, prefix = ''): readonly Resolved[] =>
  shape.map((c: Column) =>
    isStructColumn(c)
      ? {
          canonical: c.canonical,
          description: c.description,
          kind: c.kind,
          members: Object.fromEntries(
            Object.entries(c.members).map(([member, suffix]: [string, string]) => [member, `${prefix}${suffix}`]),
          ),
        }
      : {
          source: `${prefix}${c.suffix}`,
          canonical: c.canonical,
          description: c.description,
          kind: c.kind,
          ...(c.valueType === undefined ? {} : { valueType: c.valueType }),
        },
  );

/** Instance identity — present on every view, never renamed. */
